import * as React from 'react';

import type { MessageRewriteAction } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import useGenerateEmailContent from 'bundles/enterprise-admin-messaging-v2/hooks/useGenerateEmailContent';
import { buildTonePrompt } from 'bundles/enterprise-admin-messaging-v2/utils/toneInstructions';

type Params = {
  /** Current message body. The first triggerRewrite call snapshots this. */
  message: string;
  /** Called with the rewritten body (and with the snapshot on reset). */
  onMessageChange: (next: string) => void;
};

type Result = {
  /** Which pill is currently applied (or in flight). `null` = no rewrite. */
  activeAction: MessageRewriteAction | null;
  /** True while the rewrite request is in flight. */
  isRewriting: boolean;
  /** True once any rewrite has been applied (Reset original is meaningful). */
  hasSnapshot: boolean;
  /** Kick off a rewrite for the given action. */
  triggerRewrite: (action: MessageRewriteAction) => void;
  /** Restore the snapshot taken before the first rewrite. */
  resetOriginal: () => void;
};

/**
 * Tone-rewrite flow on the Review and send screen.
 *
 * Each pill reuses the `generateEmailContent` API: the original email body is
 * prefixed with a tone instruction (see `buildTonePrompt`) and sent as the
 * prompt; only the regenerated body is applied back. Every tone re-runs
 * against the SAME original body (tones replace, they don't compound), so the
 * original is captured once in a ref that survives re-renders.
 *
 * The hook owns:
 *   - `activeAction`      — which pill is currently applied.
 *   - `isRewriting`       — true while waiting for the rewrite to land.
 *   - originalMessageRef  — the message body the FIRST time any pill is
 *                           clicked. Reset original restores this, and every
 *                           rewrite is built from it. A ref (not state) so its
 *                           value can't drift across re-renders.
 *   - latestRequestId     — monotonic counter so a slow rewrite that finishes
 *                           after a newer pill click (or after Reset) is
 *                           discarded.
 */
const useToneRewrite = ({ message, onMessageChange }: Params): Result => {
  const { generate } = useGenerateEmailContent();
  const [activeAction, setActiveAction] = React.useState<MessageRewriteAction | null>(null);
  const [isRewriting, setIsRewriting] = React.useState(false);
  // The pristine body to re-tone from, captured once and kept in a ref so it
  // is stable across re-renders. `hasSnapshot` mirrors "ref is set" for render.
  const originalMessageRef = React.useRef<string | null>(null);
  const [hasSnapshot, setHasSnapshot] = React.useState(false);
  const latestRequestId = React.useRef(0);

  const triggerRewrite = React.useCallback(
    (action: MessageRewriteAction) => {
      // Capture the original body once — the first pill click defines the
      // baseline that every subsequent tone (and Reset) refers back to.
      const isFirstRewrite = originalMessageRef.current === null;
      if (isFirstRewrite) {
        originalMessageRef.current = message;
        setHasSnapshot(true);
      }
      const originalBody = originalMessageRef.current ?? message;

      setActiveAction(action);
      setIsRewriting(true);

      const requestId = latestRequestId.current + 1;
      latestRequestId.current = requestId;

      const recoverFromFailure = () => {
        if (latestRequestId.current !== requestId) {
          return;
        }
        // Clear loading + active pill so the user can retry. If this was the
        // first rewrite, drop the snapshot we just took — it equals the
        // current body, so Reset original would be a no-op.
        setIsRewriting(false);
        setActiveAction(null);
        if (isFirstRewrite) {
          originalMessageRef.current = null;
          setHasSnapshot(false);
        }
      };

      // Fire-and-forget: triggerRewrite stays `(action) => void` for the pill
      // onClick, while the work runs in an async helper.
      const run = async () => {
        try {
          const result = await generate(buildTonePrompt(action, originalBody));
          // Ignore a stale rewrite that resolved after a newer click or Reset.
          if (latestRequestId.current !== requestId) {
            return;
          }
          if (result.status === 'success') {
            onMessageChange(result.content.body);
            setIsRewriting(false);
            return;
          }
          recoverFromFailure();
        } catch {
          recoverFromFailure();
        }
      };

      void run();
    },
    [message, generate, onMessageChange]
  );

  const resetOriginal = React.useCallback(() => {
    if (originalMessageRef.current === null) {
      return;
    }
    latestRequestId.current += 1;
    onMessageChange(originalMessageRef.current);
    originalMessageRef.current = null;
    setHasSnapshot(false);
    setActiveAction(null);
    setIsRewriting(false);
  }, [onMessageChange]);

  return {
    activeAction,
    isRewriting,
    hasSnapshot,
    triggerRewrite,
    resetOriginal,
  };
};

export default useToneRewrite;
